import { memo } from 'react';
import { paymentRelatedScenarios } from '../config';
import { FV } from './share';
import FormField from 'components/FormField';
import { Radio, Select } from 'antd';
import { DisabledProps, PlatformProps, RegionProps, ScenarioTypeProps } from '../TaskForm/share';
import { useTranslate } from '@tolgee/react';
import { isAeProduct } from 'productLine';

export interface PayMethodFieldProps
  extends ScenarioTypeProps,
    PlatformProps,
    RegionProps,
    DisabledProps {}

function PayMethodField(props: PayMethodFieldProps) {
  const { platform, region, disabled } = props;

  const { t } = useTranslate();

  const label = t('aide.限定支付方式');

  return (
    <FormField<FV['limitRule']['limitPayMethod']>
      required
      name="limitRule.limitPayMethod"
      label={label}
      validate={(value) => {
        const { enable = false, payMethods = [] } = value || {};

        if (enable && payMethods.length === 0) {
          return t('department.请选择x', { x: label });
        }
      }}
    >
      {({ field: { value, onChange, onBlur } }) => {
        const { enable = false, payMethods = [] } = value || {};

        const radios = (
          <Radio.Group
            disabled={disabled}
            value={enable ? 'limit' : 'all'}
            onChange={({ target: { value } }) =>
              onChange({
                ...value,
                enable: value === 'limit',
              })
            }
            style={{ display: 'block', marginBottom: '8px' }}
          >
            <Radio value="all">{t('aide.全部支付方式')}</Radio>
            <Radio value="limit">{label}</Radio>
          </Radio.Group>
        );

        const {
          [platform]: { [region]: methods = [] },
        } = payMethodConfig;

        const select = enable && (
          <Select
            allowClear
            showArrow
            disabled={disabled}
            dropdownMatchSelectWidth={false}
            mode="multiple"
            placeholder={t('department.请选择x', { x: label })}
            value={payMethods}
            onChange={(payMethods) =>
              onChange({
                ...value,
                payMethods,
              })
            }
            onBlur={onBlur}
            style={{ width: 'auto', minWidth: '360px', maxWidth: '720px' }}
            options={methods.map((method) => {
              return {
                value: method,
                label: t(`payMethods.${method}`),
              };
            })}
          />
        );

        return [<div className="mt-[4px]">{radios}</div>, select];
      }}
    </FormField>
  );
}

export default memo((props: PayMethodFieldProps) => {
  const enable =
    !isAeProduct &&
    props.platform !== 'MERCADO' &&
    paymentRelatedScenarios.includes(props.scenarioType);

  return enable ? <PayMethodField {...props} /> : <></>;
});

const payMethodConfig: Record<
  General.PlatformUpperCase,
  Partial<Record<General.UpperCaseRegion, string[]>>
> = {
  SHOPEE: {
    BR: ['信用卡/借记卡', 'ShopeePay', 'Pix', '银行单据', 'SPayLater', '其他'],
    ID: [
      'ShopeePay',
      'COD',
      'SPayLater',
      '信用卡/借记卡',
      'Transfer Bank',
      'Akulaku PayLater',
      '其他',
    ],
    MX: ['信用卡/借记卡', '现金支付-OXXO', '网上银行-SPEI', '其他'],
    CL: ['在线支付', 'Webpay', '信用卡', 'Servipag'],
    CO: ['信用卡/借记卡', 'PSE-在线支付', 'Efecty-现金支付', '其他'],
    MY: [
      'ShopeePay',
      'COD',
      'SPayLater',
      '便利店现金支付',
      '信用卡/借记卡',
      'Maybank2u',
      '苹果支付',
      '谷歌支付',
      '其他',
    ],
    PH: ['COD', 'SPayLater', 'ShopeePay', '信用卡/借记卡', '谷歌支付', '其他'],
    SG: [
      '信用卡/借记卡',
      'ShopeePay',
      'PayNow',
      'DBS PayLah!',
      '苹果支付',
      'SPayLater',
      '谷歌支付',
      'eGIRO (Linked Bank  Account)',
      '其他',
    ],
    TH: ['COD', 'SPayLater', 'ShopeePay', '二维码PromptPay', '手机银行', '信用卡/借记卡', '其他'],
    VN: ['COD', 'ShopeePay', '信用卡/借记卡', '苹果支付', 'SPayLater', '其他'],
  },
  LAZADA: {
    ID: ['COD', '非COD'],
    MY: ['COD', '非COD'],
    PH: ['COD', '非COD'],
    SG: ['COD', '非COD'],
    TH: ['COD', '非COD'],
    VN: ['COD', '非COD'],
  },
  TIKTOK: {
    ID: ['COD', '非COD'],
    MY: ['COD', '非COD'],
    PH: ['COD', '非COD'],
    SG: ['COD', '非COD'],
    TH: ['COD', '非COD'],
    VN: ['COD', '非COD'],
  },
  TIKTOK_US: {
    US: ['COD', '非COD'],
  },
  TOKOPEDIA: {
    ID: ['COD', '非COD'],
  },
  ALIEXPRESS: {
    GB: ['Boleto账单支付', '其他'],
  },
  MERCADO: {},
};
